import { MoreVert } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import StudentAssignmentsPanel from "./StudentAssignmentsPanel";
import StudentDaySummary from "./StudentDaySummary";
import { studentStudyDate } from "../utils/studentStudyDate";

import type { StudentRelationship } from "@lingoshade/domain";

interface Props {
  entry: StudentRelationship;
  busy: boolean;
  canTeach: boolean;
  atLimit: boolean;
  onActivity: () => void;
  assignmentsOpen?: boolean;
  onAssignments: () => void;
  onAccept: () => void;
  onReject: () => void;
  onResend: () => void;
  onRequestFull: () => void;
  onRemove: () => void;
}

interface TeacherRowProps {
  entry: StudentRelationship;
  busy: boolean;
  onAllowFull: () => void;
  onDenyFull: () => void;
  onLeave: () => void;
}

export function TeacherRelationshipRow({
  entry,
  busy,
  onAllowFull,
  onDenyFull,
  onLeave,
}: TeacherRowProps): React.ReactElement {
  const { t } = useTranslation("texts");
  const name =
    [entry.teacher.lastName, entry.teacher.firstName]
      .filter(Boolean)
      .join(" ") ||
    entry.teacher.email ||
    t("materials.teacher");
  if (entry.status === "INVITED")
    return (
      <Box
        sx={{
          p: 2,
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "background.paper",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography fontWeight={600} sx={{ overflowWrap: "anywhere" }}>
              {t("students.invitedBy", { teacher: name })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("students.invitationBenefit")}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button
              component={Link}
              to={`/teacher-invitations/${entry.id}`}
              variant="contained"
            >
              {t("students.acceptInvitation")}
            </Button>
            <Button disabled={busy} onClick={onLeave}>
              {t("students.reject")}
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  return (
    <Box
      sx={{
        p: 2,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
      >
        <Box>
          <Typography fontWeight={600}>{name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t(`students.status.${entry.status}`)}
          </Typography>
        </Box>
        <Button size="small" color="error" disabled={busy} onClick={onLeave}>
          {t("students.leave")}
        </Button>
      </Stack>
      {entry.status === "ACCEPTED" && (
        <Box sx={{ mt: 1 }}>
          {entry.fullActivityRequested && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t("students.fullRequest")}
            </Typography>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={entry.fullActivityConsent}
                disabled={busy}
                onChange={(event): void => {
                  if (event.target.checked) onAllowFull();
                  else onDenyFull();
                }}
              />
            }
            label={t("students.fullConsentLabel")}
          />
          <Typography variant="body2" color="text.secondary">
            {t("students.fullConsentTerms")}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function statusKey(entry: StudentRelationship): string {
  if (entry.status === "INVITED")
    return `students.delivery.${entry.deliveryStatus}`;
  return `students.status.${entry.status}`;
}

export default function StudentRow({
  entry,
  busy,
  canTeach,
  atLimit,
  onActivity,
  assignmentsOpen,
  onAssignments,
  onAccept,
  onReject,
  onResend,
  onRequestFull,
  onRemove,
}: Props): React.ReactElement {
  const { t, i18n } = useTranslation("texts");
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const perform = (action: () => void): void => {
    setAnchor(null);
    action();
  };
  return (
    <Box
      component="li"
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr) auto",
          md: "minmax(0, 1fr) auto",
        },
        alignItems: "center",
        columnGap: 2,
        px: 2,
        py: 1,
        minHeight: 56,
        borderBottom: 1,
        borderColor: "divider",
        "&:last-child": { borderBottom: 0 },
      }}
    >
      <Box
        sx={{
          minWidth: 0,
          gridColumn: { xs: entry.status === "ACCEPTED" ? "1 / -1" : 1, md: 1 },
          gridRow: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { md: "baseline" },
          gap: { xs: 0, md: 2 },
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, overflowWrap: "anywhere", minWidth: 0 }}
        >
          {entry.email}
        </Typography>
        {entry.status === "ACCEPTED" && canTeach && entry.lastStudyDay && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ flexShrink: 0 }}
          >
            {studentStudyDate(
              entry.lastStudyDay.lastActiveAt,
              i18n.language,
              t("students.lastDay.today"),
              t("students.lastDay.yesterday"),
            )}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          gridColumn: { xs: entry.status === "ACCEPTED" ? "1 / -1" : 1, md: 1 },
          gridRow: { xs: entry.status === "ACCEPTED" ? 3 : 2, md: 2 },
          minWidth: 0,
        }}
      >
        {entry.status === "ACCEPTED" ? (
          <StudentDaySummary day={entry.lastStudyDay} canTeach={canTeach} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t(statusKey(entry))}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gridColumn: { xs: entry.status === "ACCEPTED" ? "1 / -1" : 2, md: 2 },
          gridRow: {
            xs: entry.status === "ACCEPTED" ? 2 : "1 / span 2",
            md: "1 / span 2",
          },
        }}
      >
        {entry.status === "ACCEPTED" && (
          <Button
            size="small"
            disabled={busy}
            onClick={onAssignments}
            aria-expanded={Boolean(assignmentsOpen)}
          >
            {t("students.assignments.title")}
          </Button>
        )}
        {entry.status === "ACCEPTED" && (
          <Button
            size="small"
            disabled={busy || !canTeach}
            onClick={onActivity}
          >
            {t("students.viewReport")}
          </Button>
        )}
        {entry.status === "REQUESTED" && (
          <Button
            size="small"
            disabled={busy || !canTeach || atLimit}
            onClick={onAccept}
          >
            {t("students.accept")}
          </Button>
        )}
        <IconButton
          size="small"
          disabled={busy}
          aria-label={t("students.rowActions", { email: entry.email })}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchor)}
          onClick={(event): void => setAnchor(event.currentTarget)}
        >
          <MoreVert />
        </IconButton>
      </Box>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={(): void => setAnchor(null)}
        slotProps={{ paper: { sx: { maxWidth: 360 } } }}
      >
        {entry.status === "ACCEPTED" && (
          <MenuItem
            disabled={
              !canTeach ||
              entry.fullActivityRequested ||
              entry.fullActivityConsent
            }
            sx={{ whiteSpace: "normal" }}
            onClick={(): void => perform(onRequestFull)}
          >
            {t(
              entry.fullActivityConsent
                ? "students.fullEnabled"
                : entry.fullActivityRequested
                  ? "students.fullPending"
                  : "students.requestFull",
            )}
          </MenuItem>
        )}
        {entry.status === "REQUESTED" && (
          <MenuItem onClick={(): void => perform(onReject)}>
            {t("students.reject")}
          </MenuItem>
        )}
        {["INVITED", "REJECTED"].includes(entry.status) && (
          <MenuItem
            disabled={!canTeach || atLimit}
            onClick={(): void => perform(onResend)}
          >
            {t("students.resend")}
          </MenuItem>
        )}
        <MenuItem
          sx={{ color: "error.main" }}
          onClick={(): void => perform(onRemove)}
        >
          {t(
            entry.status === "ACCEPTED"
              ? "students.remove"
              : entry.status === "REJECTED"
                ? "students.removeFromList"
                : "students.cancelInvitation",
          )}
        </MenuItem>
      </Menu>
      {entry.status === "ACCEPTED" && assignmentsOpen && (
        <StudentAssignmentsPanel entry={entry} canTeach={canTeach} />
      )}
    </Box>
  );
}
